import React from 'react';
import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';


export interface SegmentedSwitchOption {
  value: string;
  label: string;
}

export interface SegmentedSwitchProps {
  /** Exactly two options — this is a binary segmented control. */
  options: [SegmentedSwitchOption, SegmentedSwitchOption];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  'data-testid'?: string;
}

/**
 * Segmented control with exactly two pill-shaped options (e.g. Activos / Inactivos).
 * The selected option renders as a white inner pill with brand-colored text;
 * the unselected option renders as plain gray text with no background.
 * Unlike `Switch`, this is not a boolean ON/OFF toggle — both options are
 * always labeled and either can be actively selected.
 */
export const SegmentedSwitch: React.FC<SegmentedSwitchProps> = ({
  options,
  value,
  onChange,
  className = '',
  'data-testid': dataTestId,
}) => (
  <div
    role="radiogroup"
    className={`inline-flex items-center ${className}`}
    style={{
      backgroundColor: colors.primary.background2,
      borderRadius: '16px',
      padding: '4px',
    }}
    data-testid={dataTestId}
  >
    {options.map((option) => {
      const isSelected = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={isSelected}
          onClick={() => {
            if (!isSelected) onChange(option.value);
          }}
          style={{
            border: 'none',
            cursor: 'pointer',
            borderRadius: '16px',
            padding: '2px 16px',
            backgroundColor: isSelected ? colors.grays.neutralFF : 'transparent',
            transition: 'background-color 500ms ease-in',
          }}
          data-testid={dataTestId ? `${dataTestId}-${option.value}` : undefined}
        >
          <Text
            variant="subheader-medium"
            style={{ transition: 'color 500ms ease-in' }}
            color={isSelected ? colors.important.main : colors.primary.auxiliar}
            as="span"
          >
            {option.label}
          </Text>
        </button>
      );
    })}
  </div>
);

export default SegmentedSwitch;
